import { useEffect, useState, Fragment } from "react";
import { ChevronDown, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { prettyType } from "@/lib/defects";

const GOLD = "#C5A24A";

function MiniChart({ series }) {
  if (!series || series.length < 2) return null;
  const w = 280, h = 64, pad = 6;
  const max = Math.max(1, ...series.map((s) => s.total));
  const xs = (i) => pad + (i * (w - pad * 2)) / (series.length - 1);
  const ys = (v) => h - pad - (v / max) * (h - pad * 2);
  const line = (key) => series.map((s, i) => `${xs(i)},${ys(s[key])}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" data-testid="summary-chart">
      <polyline points={line("total")} fill="none" stroke="#E5E7EB" strokeWidth="1.6" />
      <polyline points={line("high_crit")} fill="none" stroke="#EA580C" strokeWidth="1.6" />
      <polyline points={line("resolved")} fill="none" stroke={GOLD} strokeWidth="1.6" />
    </svg>
  );
}

function Tile({ label, value, color }) {
  return (
    <div className="border border-white/10 rounded p-2 text-center bg-[#121212]">
      <p className="font-display text-xl font-extrabold" style={{ color }}>{value}</p>
      <p className="label-caps text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

export default function InspectionSummary({ projectId, refreshKey, onSaveBaseline }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api.get(`/projects/${projectId}/summary`).then((r) => setData(r.data)).catch(() => {});
  }, [projectId, refreshKey]);

  if (!data) return null;
  const { overall, by_type, prev_date, timeseries } = data;

  return (
    <div className="border-b border-white/10 bg-[#0E0E0E] shrink-0" data-testid="inspection-summary">
      <button onClick={() => setOpen(!open)} data-testid="summary-toggle" className="w-full flex items-center justify-between px-4 py-3">
        <span className="font-display font-semibold text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" style={{ color: GOLD }} /> Inspection Summary</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 max-h-[42vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-2">
            <Tile label="New" value={overall.new} color="#C5A24A" />
            <Tile label="Worsening" value={overall.worsening} color="#EA580C" />
            <Tile label="Resolved" value={overall.resolved} color="#10B981" />
          </div>
          <p className="text-[10px] text-muted-foreground">
            {prev_date
              ? `Compared to inspection on ${prev_date}. Save Inspection to set a new baseline.`
              : "No previous inspection yet — Save Inspection to start tracking change over time."}
          </p>
          {!prev_date && overall.total > 0 && onSaveBaseline && (
            <div className="rounded-md border border-[#C5A24A]/40 bg-[#C5A24A]/10 p-2.5" data-testid="baseline-nudge">
              <p className="text-[11px] text-[#D8BE86] leading-snug">Save your first inspection to lock in a baseline — then this panel tracks New / Worsening / Resolved over time.</p>
              <button onClick={onSaveBaseline} data-testid="summary-save-baseline" className="mt-2 text-[11px] px-2.5 py-1 rounded bg-[#C5A24A] text-black font-medium hover:bg-[#A8863A] transition-colors">Save Inspection</button>
            </div>
          )}
          <MiniChart series={timeseries} />
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-[#E5E7EB]" /> Total</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-[#EA580C]" /> High+Crit</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-0.5" style={{ background: GOLD }} /> Resolved</span>
          </div>
          {by_type.length > 0 && (
            <div className="grid grid-cols-[1.4fr_0.6fr_0.6fr_0.7fr] text-xs gap-y-1">
              <span className="label-caps text-muted-foreground">Defect</span>
              <span className="label-caps text-muted-foreground text-right">New</span>
              <span className="label-caps text-muted-foreground text-right">Wors</span>
              <span className="label-caps text-muted-foreground text-right">Resv</span>
              {by_type.map((r) => (
                <Fragment key={r.type}>
                  <span className="py-1 border-t border-white/10 truncate">{prettyType(r.type)}</span>
                  <span className="py-1 border-t border-white/10 text-right font-mono">{r.total}</span>
                  <span className="py-1 border-t border-white/10 text-right font-mono" style={{ color: "#EA580C" }}>{r.worsening}</span>
                  <span className="py-1 border-t border-white/10 text-right font-mono" style={{ color: GOLD }}>{r.resolved}</span>
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
